import path from "node:path";
import { InvalidArgumentError } from "commander";
import type { Command } from "commander";
import {
  DEFAULT_AGENT_NAME,
  normalizeAgentName,
  resolveCanonicalAgentName,
  resolveAgentArgv,
  resolveAgentCommand as resolveAgentCommandFromRegistry,
} from "../agent-registry.js";
import type { SystemPromptOption } from "../runtime/engine/session-options.js";
import { DEFAULT_QUEUE_OWNER_TTL_MS } from "../session/session.js";
import {
  AUTH_POLICIES,
  NON_INTERACTIVE_PERMISSION_POLICIES,
  OUTPUT_FORMATS,
  type AuthPolicy,
  type NonInteractivePermissionPolicy,
  type OutputFormat,
  type OutputPolicy,
  type PermissionMode,
} from "../types.js";
import type { ResolvedAcpxConfig } from "./config.js";
import { toTimerMilliseconds } from "./timer-duration.js";

export type PermissionFlags = {
  approveAll?: boolean;
  approveReads?: boolean;
  denyAll?: boolean;
};

export function hasExplicitPermissionModeFlag(flags: PermissionFlags): boolean {
  return flags.approveAll === true || flags.approveReads === true || flags.denyAll === true;
}

export type GlobalFlags = PermissionFlags & {
  agent?: string;
  cwd: string;
  authPolicy?: AuthPolicy;
  nonInteractivePermissions: NonInteractivePermissionPolicy;
  jsonStrict?: boolean;
  suppressReads?: boolean;
  fs?: boolean;
  terminal?: boolean;
  timeout?: number;
  ttl: number;
  verbose?: boolean;
  format: OutputFormat;
  model?: string;
  allowedTools?: string[];
  maxTurns?: number;
  systemPrompt?: SystemPromptOption;
  promptRetries?: number;
  permissionPolicy?: string;
};

export type PromptFlags = {
  session?: string;
  wait?: boolean;
  file?: string;
};

export type ExecFlags = {
  file?: string;
  configOption?: SessionConfigOptionAssignment[];
};

export type SessionConfigOptionAssignment = {
  configId: string;
  value: string;
};

export type SessionsNewFlags = {
  name?: string;
  resumeSession?: string;
};

export type SessionsHistoryFlags = {
  limit: number;
};

export type SessionsListFlags = {
  cursor?: string;
  filterCwd?: string;
  local?: boolean;
};

export type SessionsExportFlags = {
  output: string;
  sourceCwd?: string;
};

export type SessionsImportFlags = {
  name?: string;
  destinationCwd?: string;
};

export type StatusFlags = {
  session?: string;
};

type SessionSelectionFlags = {
  session?: string;
};

export type SessionsPruneFlags = {
  dryRun?: boolean;
  before?: Date;
  olderThan?: number;
  includeHistory?: boolean;
};

function stringOption(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function numberOption(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

function stringArrayOption(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string")
    ? value
    : undefined;
}

function nonEmptyStringOption(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function parseOutputFormat(value: string): OutputFormat {
  if (!OUTPUT_FORMATS.includes(value as OutputFormat)) {
    throw new InvalidArgumentError(
      `Invalid format "${value}". Expected one of: ${OUTPUT_FORMATS.join(", ")}`,
    );
  }
  return value as OutputFormat;
}

export function parseAuthPolicy(value: string): AuthPolicy {
  if (!AUTH_POLICIES.includes(value as AuthPolicy)) {
    throw new InvalidArgumentError(
      `Invalid auth policy "${value}". Expected one of: ${AUTH_POLICIES.join(", ")}`,
    );
  }
  return value as AuthPolicy;
}

export function parseNonInteractivePermissionPolicy(value: string): NonInteractivePermissionPolicy {
  if (!NON_INTERACTIVE_PERMISSION_POLICIES.includes(value as NonInteractivePermissionPolicy)) {
    throw new InvalidArgumentError(
      `Invalid non-interactive permission policy "${value}". Expected one of: ${NON_INTERACTIVE_PERMISSION_POLICIES.join(", ")}`,
    );
  }
  return value as NonInteractivePermissionPolicy;
}

export function parseTimeoutSeconds(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new InvalidArgumentError("Timeout must be a positive number of seconds");
  }
  const milliseconds = toTimerMilliseconds(parsed, false);
  if (milliseconds === undefined) {
    throw new InvalidArgumentError("Timeout exceeds the maximum supported timer delay");
  }
  return milliseconds;
}

export function parseTtlSeconds(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new InvalidArgumentError("TTL must be a non-negative number of seconds");
  }
  const milliseconds = toTimerMilliseconds(parsed, true);
  if (milliseconds === undefined) {
    throw new InvalidArgumentError("TTL exceeds the maximum supported timer delay");
  }
  return milliseconds;
}

export function parseSessionName(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new InvalidArgumentError("Session name must not be empty");
  }
  return trimmed;
}

export function parseNonEmptyValue(label: string, value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new InvalidArgumentError(`${label} must not be empty`);
  }
  return trimmed;
}

export function parseSessionConfigOptionAssignment(value: string): SessionConfigOptionAssignment {
  const separator = value.indexOf("=");
  if (separator <= 0 || separator === value.length - 1) {
    throw new InvalidArgumentError(
      'Session config option must use "<key>=<value>" with non-empty parts',
    );
  }

  const configId = value.slice(0, separator).trim();
  const configValue = value.slice(separator + 1).trim();
  if (configId.length === 0 || configValue.length === 0) {
    throw new InvalidArgumentError(
      'Session config option must use "<key>=<value>" with non-empty parts',
    );
  }
  return { configId, value: configValue };
}

function collectSessionConfigOptionAssignment(
  value: string,
  previous: SessionConfigOptionAssignment[] = [],
): SessionConfigOptionAssignment[] {
  return [...previous, parseSessionConfigOptionAssignment(value)];
}

export function parseHistoryLimit(value: string): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new InvalidArgumentError("Limit must be a positive integer");
  }
  return parsed;
}

export function parseDaysOlderThan(value: string): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new InvalidArgumentError("--older-than must be a positive integer number of days");
  }
  return parsed;
}

export function parsePruneBeforeDate(value: string): Date {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new InvalidArgumentError(
      `--before must be a valid date (e.g. 2026-01-01 or 2026-01-01T00:00:00Z)`,
    );
  }
  return date;
}

export function parseAllowedTools(value: string): string[] {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return [];
  }

  const items = trimmed.split(",").map((item) => item.trim());
  if (items.some((item) => item.length === 0)) {
    throw new InvalidArgumentError(
      "Allowed tools must be a comma-separated list without empty entries",
    );
  }

  return items;
}

export function parseMaxTurns(value: string): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new InvalidArgumentError("Max turns must be a positive integer");
  }
  return parsed;
}

export function resolveSystemPromptFlag(opts: {
  systemPrompt?: unknown;
  appendSystemPrompt?: unknown;
}): SystemPromptOption | undefined {
  const replace = nonEmptyStringOption(opts.systemPrompt);
  const append = nonEmptyStringOption(opts.appendSystemPrompt);

  if (replace !== undefined && append !== undefined) {
    throw new InvalidArgumentError("Use only one of --system-prompt or --append-system-prompt");
  }
  if (replace !== undefined) {
    return replace;
  }
  if (append !== undefined) {
    return { append };
  }
  return undefined;
}

export function parsePromptRetries(value: string): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new InvalidArgumentError("Prompt retries must be a non-negative integer");
  }
  return parsed;
}

export function resolvePermissionMode(
  flags: PermissionFlags,
  defaultMode: PermissionMode,
): PermissionMode {
  const selected = [flags.approveAll, flags.approveReads, flags.denyAll].filter(Boolean).length;

  if (selected > 1) {
    throw new InvalidArgumentError(
      "Use only one permission mode: --approve-all, --approve-reads, or --deny-all",
    );
  }

  if (flags.approveAll) {
    return "approve-all";
  }
  if (flags.approveReads) {
    return "approve-reads";
  }
  if (flags.denyAll) {
    return "deny-all";
  }

  return defaultMode;
}

export function addGlobalFlags(command: Command): Command {
  return command
    .option("--agent <command>", "Raw ACP agent command (escape hatch)")
    .option("--cwd <dir>", "Working directory", process.cwd())
    .option(
      "--auth-policy <policy>",
      "Authentication policy: skip or fail when auth is required",
      parseAuthPolicy,
    )
    .option("--approve-all", "Auto-approve all permission requests")
    .option("--approve-reads", "Auto-approve read/search requests and prompt for writes")
    .option("--deny-all", "Deny all permission requests")
    .option(
      "--non-interactive-permissions <policy>",
      "When prompting is unavailable: deny or fail",
      parseNonInteractivePermissionPolicy,
    )
    .option(
      "--permission-policy <json-or-file>",
      "Permission policy JSON or path (autoApprove, autoDeny, escalate, defaultAction)",
    )
    .option("--policy <json-or-file>", "Alias for --permission-policy")
    .option("--format <fmt>", "Output format: text, json, quiet", parseOutputFormat)
    .option("--suppress-reads", "Suppress raw read-file contents in output")
    .option("--model <id>", "Agent model id")
    .option(
      "--allowed-tools <list>",
      'Allowed tool names as a comma-separated list (use "" for no tools)',
      parseAllowedTools,
    )
    .option("--max-turns <count>", "Maximum turns for the session", parseMaxTurns)
    .option(
      "--system-prompt <text>",
      "Replace the agent system prompt (claude-agent-acp via ACP _meta.systemPrompt)",
      (value: string) => parseNonEmptyValue("System prompt", value),
    )
    .option(
      "--append-system-prompt <text>",
      "Append text to the agent system prompt (claude-agent-acp via ACP _meta.systemPrompt.append)",
      (value: string) => parseNonEmptyValue("Append system prompt", value),
    )
    .option(
      "--prompt-retries <count>",
      "Retry failed prompt turns on transient errors (default: 0)",
      parsePromptRetries,
    )
    .option(
      "--json-strict",
      "Strict JSON mode: requires --format json and suppresses non-JSON stderr output",
    )
    .option("--no-fs", "Do not advertise ACP filesystem capabilities")
    .option("--no-terminal", "Do not advertise ACP terminal capability")
    .option("--timeout <seconds>", "Maximum time to wait for agent response", parseTimeoutSeconds)
    .option(
      "--ttl <seconds>",
      "Queue owner idle TTL before shutdown (0 = keep alive forever) (default: 300)",
      parseTtlSeconds,
    )
    .option(
      "--mcp-config <path>",
      "Load MCP servers from a JSON config file instead of project/global mcpServers",
    )
    .option("--verbose", "Enable verbose debug logs");
}

export function addSessionOption(command: Command): Command {
  return command
    .option("-s, --session <name>", "Use named session instead of cwd default", parseSessionName)
    .option(
      "--no-wait",
      "Queue prompt and return immediately when another prompt is already running",
    );
}

export function addSessionNameOption(command: Command): Command {
  return command.option(
    "-s, --session <name>",
    "Use named session instead of cwd default",
    parseSessionName,
  );
}

export function resolveSessionNameFromFlags(
  flags: SessionSelectionFlags,
  command: Command,
): string | undefined {
  const directSession = parseOptionalSessionName(flags.session);
  if (directSession !== undefined) {
    return directSession;
  }

  // Commander parses options on the parent command when flags appear before the
  // subcommand (e.g. `acpx codex -s foo cancel`). Use optsWithGlobals() so
  // subcommands can still access those values.
  const allOpts = command.optsWithGlobals<Record<string, unknown>>();
  return parseOptionalSessionName(allOpts.session);
}

function parseOptionalSessionName(value: unknown): string | undefined {
  const session = stringOption(value);
  return session === undefined ? undefined : parseSessionName(session);
}

function resolveCommandOption(command: Command, key: string, fallback: unknown): unknown {
  // Explicit child values win; a child's --no-wait default must not hide a parent flag.
  for (let current: Command | null = command; current; current = current.parent) {
    if (current.getOptionValueSource(key) === "cli") {
      return current.opts<Record<string, unknown>>()[key];
    }
  }
  return fallback;
}

export function resolvePromptFlags(flags: PromptFlags, command: Command): PromptFlags {
  return {
    ...flags,
    file: stringOption(resolveCommandOption(command, "file", flags.file)),
    wait: resolveCommandOption(command, "wait", flags.wait) !== false,
  };
}

export function resolveSessionsListFlags(
  flags: SessionsListFlags,
  command: Command,
): SessionsListFlags {
  return {
    cursor: stringOption(resolveCommandOption(command, "cursor", flags.cursor)),
    filterCwd: stringOption(resolveCommandOption(command, "filterCwd", flags.filterCwd)),
    local: resolveCommandOption(command, "local", flags.local) === true,
  };
}

export function addPromptInputOption(command: Command): Command {
  return command.option("-f, --file <path>", "Read prompt text from file path (use - for stdin)");
}

export function addExecConfigOption(command: Command): Command {
  return command.option(
    "--config-option <key=value>",
    "Set an ACP session config option before the one-shot prompt (repeatable)",
    collectSessionConfigOptionAssignment,
  );
}

export function resolveGlobalFlags(command: Command, config: ResolvedAcpxConfig): GlobalFlags {
  const opts = command.optsWithGlobals<Record<string, unknown>>();
  const format = parseOutputFormat(
    opts.json === true ? "json" : (stringOption(opts.format) ?? config.format ?? "text"),
  );
  const jsonStrict = opts.jsonStrict === true;
  const verbose = opts.verbose === true;
  assertOutputFlagCompatibility(format, jsonStrict, verbose);

  return {
    agent: resolveAgentOverride(stringOption(opts.agent)),
    cwd: resolveCwdOption(opts.cwd),
    authPolicy: resolveAuthPolicy(opts.authPolicy, config),
    nonInteractivePermissions: resolveNonInteractivePermissions(
      opts.nonInteractivePermissions,
      config,
    ),
    permissionPolicy: resolvePermissionPolicyOption(opts),
    jsonStrict,
    suppressReads: opts.suppressReads === true,
    fs: resolveCapabilityOption(opts.fs),
    terminal: resolveCapabilityOption(opts.terminal),
    timeout: resolveTimeoutOption(opts.timeout, config),
    ttl: resolveTtlOption(opts.ttl, config),
    verbose,
    format,
    model: resolveModelOption(opts.model),
    allowedTools: stringArrayOption(opts.allowedTools),
    maxTurns: numberOption(opts.maxTurns),
    systemPrompt: resolveSystemPromptFlag(opts),
    promptRetries: numberOption(opts.promptRetries),
    approveAll: opts.approveAll ? true : undefined,
    approveReads: opts.approveReads ? true : undefined,
    denyAll: opts.denyAll ? true : undefined,
  };
}

function resolveCwdOption(value: unknown): string {
  return stringOption(value) ?? process.cwd();
}

function resolveAuthPolicy(optsValue: unknown, config: ResolvedAcpxConfig): AuthPolicy {
  const value = stringOption(optsValue);
  return value === undefined ? config.authPolicy : parseAuthPolicy(value);
}

function resolveNonInteractivePermissions(
  optsValue: unknown,
  config: ResolvedAcpxConfig,
): NonInteractivePermissionPolicy {
  const value = stringOption(optsValue);
  return value === undefined
    ? config.nonInteractivePermissions
    : parseNonInteractivePermissionPolicy(value);
}

function resolvePermissionPolicyOption(opts: Record<string, unknown>): string | undefined {
  const primary = stringOption(opts.permissionPolicy);
  const alias = stringOption(opts.policy);
  if (primary !== undefined && alias !== undefined && primary !== alias) {
    throw new InvalidArgumentError(
      "Use only one permission policy flag: --permission-policy or --policy",
    );
  }
  return primary ?? alias;
}

function resolveCapabilityOption(value: unknown): boolean | undefined {
  return value === false ? false : undefined;
}

function resolveTimeoutOption(value: unknown, config: ResolvedAcpxConfig): number | undefined {
  return numberOption(value) ?? config.timeoutMs;
}

function resolveTtlOption(value: unknown, config: ResolvedAcpxConfig): number {
  return numberOption(value) ?? config.ttlMs ?? DEFAULT_QUEUE_OWNER_TTL_MS;
}

function assertOutputFlagCompatibility(
  format: OutputFormat,
  jsonStrict: boolean,
  verbose: boolean,
): void {
  if (jsonStrict && format !== "json") {
    throw new InvalidArgumentError("--json-strict requires --format json");
  }

  if (jsonStrict && verbose) {
    throw new InvalidArgumentError("--json-strict cannot be combined with --verbose");
  }
}

function resolveModelOption(value: unknown): string | undefined {
  const model = stringOption(value);
  return model === undefined ? undefined : parseNonEmptyValue("Model", model);
}

function resolveAgentOverride(value: string | undefined): string | undefined {
  return value === undefined ? undefined : parseNonEmptyValue("Agent command", value);
}

export function resolveOutputPolicy(format: OutputFormat, jsonStrict: boolean): OutputPolicy {
  return {
    format,
    jsonStrict,
    suppressReads: false,
    suppressNonJsonStderr: jsonStrict,
    queueErrorAlreadyEmitted: format !== "quiet",
    suppressSdkConsoleErrors: jsonStrict || format === "quiet",
  };
}

export function resolveAgentInvocation(
  explicitAgentName: string | undefined,
  globalFlags: GlobalFlags,
  config: ResolvedAcpxConfig,
): {
  agentName: string;
  agentCommand: string;
  agentArgv?: string[];
  cwd: string;
} {
  const override = resolveAgentOverride(globalFlags.agent);
  if (override !== undefined && explicitAgentName !== undefined) {
    throw new InvalidArgumentError("Do not combine positional agent with --agent override");
  }

  const agentName = explicitAgentName ?? config.defaultAgent ?? DEFAULT_AGENT_NAME;
  const command = resolveInvocationCommand(agentName, override, config);

  return {
    agentName,
    ...command,
    cwd: path.resolve(globalFlags.cwd),
  };
}

function resolveInvocationCommand(
  agentName: string,
  override: string | undefined,
  config: ResolvedAcpxConfig,
): { agentCommand: string; agentArgv?: string[] } {
  if (override !== undefined) {
    return { agentCommand: override };
  }
  const normalizedAgentName = normalizeAgentName(agentName);
  const canonicalAgentName = resolveCanonicalAgentName(agentName);
  const configuredAgent = Object.hasOwn(config.agents, normalizedAgentName)
    ? config.agents[normalizedAgentName]
    : Object.hasOwn(config.agents, canonicalAgentName)
      ? config.agents[canonicalAgentName]
      : undefined;
  if (configuredAgent) {
    return {
      agentCommand: configuredAgent.command,
      ...(configuredAgent.argv ? { agentArgv: [...configuredAgent.argv] } : {}),
    };
  }
  const agentArgv = resolveAgentArgv(agentName);
  return {
    agentCommand: resolveAgentCommandFromRegistry(agentName),
    ...(agentArgv ? { agentArgv } : {}),
  };
}
